import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Renderer2, NgZone } from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';
import {
  MsalModule,
  MsalService,
  MsalBroadcastService
} from '@azure/msal-angular';
import {
  InteractionStatus,
  EventMessage,
  EventType
} from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { BreadcrumbComponent } from 'xng-breadcrumb';
import { TopBarComponent } from './shared/top-bar/topbar.component';
import { AppInsightsService } from './services/app-insights.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('mask-anim', [
      state(
        'void',
        style({
          opacity: 0
        })
      ),
      state(
        'visible',
        style({
          opacity: 0.8
        })
      ),
      transition('* => *', animate('250ms cubic-bezier(0, 0, 0.2, 1)'))
    ])
  ],
  imports: [
    BreadcrumbComponent,
    CommonModule,
    MsalModule,
    RouterModule,
    RouterOutlet,
    TopBarComponent
  ],
  standalone: true
})
export class AppComponent implements OnInit, OnDestroy {
  loginDisplay = false;
  tokenExpiration = '';
  configDialogActive: boolean | undefined;
  isIframe = false;
  private readonly _destroying$ = new Subject<void>();

  menuClick: boolean | undefined;
  userMenuClick: boolean | undefined;
  topbarUserMenuActive: boolean | undefined;
  menuActive: boolean | undefined;
  menuHoverActive: boolean | undefined;
  resetMenu = false;
  horizontal = true;

  topbarColor = 'layout-topbar-black';
  menuColor = 'layout-menu-light';
  themeColor = 'black';
  layoutColor = 'black';
  topbarSize = 'small';

  constructor(
    public renderer: Renderer2,
    public zone: NgZone,
    private appInsightsService: AppInsightsService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    this.appInsightsService.init();
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;

    this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this._destroying$)
    );

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
            msg.eventType === EventType.BROKERED_REQUEST_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(msg => {
        this.tokenExpiration = (msg.payload as any).expiresOn;
        localStorage.setItem('tokenExpiration', this.tokenExpiration);
        this.authService.instance.setActiveAccount(
          (msg.payload as any).account
        );
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  selectorMatches(el: any, selector: any) {
    const matches = 'matches';
    const webkitMatchesSelector = 'webkitMatchesSelector';
    const mozMatchesSelector = 'mozMatchesSelector';
    const msMatchesSelector = 'msMatchesSelector';
    const p = Element.prototype;
    const f =
      p[matches] ||
      p[webkitMatchesSelector] ||
      p[mozMatchesSelector] ||
      p[msMatchesSelector] ||
      function (this: Element, s: string): boolean {
        return Array.from(document.querySelectorAll(s)).indexOf(this) !== -1;
      };
    return f.call(el, selector);
  }

  isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight);
  }

  rippleEffect(element, e) {
    if (element.querySelector('.ink') === null) {
      const inkEl = document.createElement('span');
      this.addClass(inkEl, 'ink');

      if (
        this.hasClass(element, 'ripplelink') &&
        element.querySelector('span')
      ) {
        element
          .querySelector('span')
          .insertAdjacentHTML('afterend', "<span class='ink'></span>");
      } else {
        element.appendChild(inkEl);
      }
    }

    const ink = element.querySelector('.ink');
    this.removeClass(ink, 'ripple-animate');

    if (!ink.offsetHeight && !ink.offsetWidth) {
      const d = Math.max(element.offsetWidth, element.offsetHeight);
      ink.style.height = d + 'px';
      ink.style.width = d + 'px';
    }

    const x = e.pageX - this.getOffset(element).left - ink.offsetWidth / 2;
    const y = e.pageY - this.getOffset(element).top - ink.offsetHeight / 2;

    ink.style.top = y + 'px';
    ink.style.left = x + 'px';
    ink.style.pointerEvents = 'none';
    this.addClass(ink, 'ripple-animate');
  }

  hasClass(element, className) {
    if (element.classList) {
      return element.classList.contains(className);
    } else {
      return new RegExp('(^| )' + className + '( |$)', 'gi').test(
        element.className
      );
    }
  }

  addClass(element, className) {
    if (element.classList) {
      element.classList.add(className);
    } else {
      element.className += ' ' + className;
    }
  }

  removeClass(element, className) {
    if (element.classList) {
      element.classList.remove(className);
    } else {
      element.className = element.className.replace(
        new RegExp(
          '(^|\\b)' + className.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  getOffset(el) {
    const rect = el.getBoundingClientRect();

    return {
      top:
        rect.top +
        (window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0),
      left:
        rect.left +
        (window.pageXOffset ||
          document.documentElement.scrollLeft ||
          document.body.scrollLeft ||
          0)
    };
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  onWrapperClick() {
    if (!this.menuClick) {
      this.menuActive = false;
    }

    if (!this.userMenuClick) {
      this.topbarUserMenuActive = false;
    }

    if (!this.menuClick) {
      if (this.horizontal) {
        this.resetMenu = true;
      }

      this.menuHoverActive = false;
      this.unblockBodyScroll();
    }

    this.userMenuClick = false;
    this.menuClick = false;
  }

  onMenuButtonClick(event: Event) {
    this.menuClick = true;

    if (!this.horizontal || this.isMobile()) {
      this.menuActive = !this.menuActive;

      if (this.menuActive) {
        this.blockBodyScroll();
      } else {
        this.unblockBodyScroll();
      }
    }

    event.preventDefault();
  }

  onTopbarUserMenuButtonClick(event) {
    this.userMenuClick = true;
    this.topbarUserMenuActive = !this.topbarUserMenuActive;

    event.preventDefault();
  }

  onTopbarUserMenuClick(event) {
    this.userMenuClick = true;

    if (
      event.target.nodeName === 'A' ||
      event.target.parentNode.nodeName === 'A'
    ) {
      this.topbarUserMenuActive = false;
    }
  }

  onTopbarSubItemClick(event) {
    event.preventDefault();
  }

  onSidebarClick() {
    this.menuClick = true;
    this.resetMenu = false;
  }

  isMobile() {
    return window.innerWidth <= 1024;
  }

  isTablet() {
    const width = window.innerWidth;
    return width <= 1024 && width > 640;
  }

  changeTopbarTheme(event, color) {
    this.topbarColor = 'layout-topbar-' + color;

    event.preventDefault();
  }

  changeMenuToHorizontal(event, mode) {
    this.horizontal = mode;

    event.preventDefault();
  }

  changeMenuTheme(event, color) {
    this.menuColor = 'layout-menu-' + color;

    event.preventDefault();
  }

  changeComponentTheme(event, theme) {
    this.themeColor = theme;
    const themeLink: HTMLLinkElement = document.getElementById(
      'theme-css'
    ) as HTMLLinkElement;
    themeLink.href = 'assets/theme/' + 'theme-' + theme + '.css';

    event.preventDefault();
  }

  changePrimaryColor(event, color) {
    this.layoutColor = color;
    const layoutLink: HTMLLinkElement = document.getElementById(
      'layout-css'
    ) as HTMLLinkElement;
    layoutLink.href = 'assets/layout/css/layout-' + color + '.css';

    event.preventDefault();
  }
}
